import React, { useState, useEffect } from 'react';
import { attendanceAPI, usersAPI } from '../../services/api';
import { format, subMonths, setDate, parseISO, getDay, isToday, subDays } from 'date-fns';
import { AlertTriangle, RefreshCw, X, Edit2, ShieldCheck, Search, Download } from 'lucide-react';
import { getStatusBadge, getRowHighlightClass, DateColumnDisplay, formatMinutes, REQUIRED_WORKDAY_MINUTES } from '../../utils/attendanceUtils';
import { RawTransactionsPanel, AuditTrailPanel } from './AttendanceEditPanels';

const ISSUE_FILTERS = [
  { id: 'all',                 label: 'All Issues' },
  { id: 'absent',               label: 'Absent' },
  { id: 'incomplete',           label: 'Incomplete' },
  { id: 'late',                 label: 'Late' },
  { id: 'early_out',            label: 'Early Out' },
  { id: 'insufficient_hours',   label: 'Insufficient Hours' },
];

const defaultStart = () => format(setDate(subMonths(new Date(), 1), 21), 'yyyy-MM-dd');
// Default end date today - 1 day
const defaultEnd = () => format(subDays(new Date(), 1), 'yyyy-MM-dd');

const getIssueTypes = (record) => {
  const issues = [];
  const dayOfWeek = getDay(parseISO(record.workday));
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isPast = !isToday(parseISO(record.workday));

  if (!isWeekend && !record.is_holiday && !record.leave_id) {
    if (!record.clock_in && !record.clock_out) issues.push('absent');
    else if (record.clock_in && !record.clock_out && isPast) issues.push('incomplete');
    else if (record.clock_in && record.clock_out && record.total_work_minutes < REQUIRED_WORKDAY_MINUTES) {
      issues.push('insufficient_hours');
    }
  }
  if (record.is_late)      issues.push('late');
  if (record.is_early_out) issues.push('early_out');

  return issues;
};

const IssueBadge = ({ type }) => {
  const styles = {
    absent:               'bg-red-100 text-red-800',
    incomplete:           'bg-yellow-100 text-yellow-800',
    late:                 'bg-orange-100 text-orange-800',
    early_out:            'bg-purple-100 text-purple-800',
    insufficient_hours:   'bg-pink-100 text-pink-800',
  };
  const labels = {
    absent:               'Absent',
    incomplete:           'Incomplete',
    late:                 'Late',
    early_out:            'Early Out',
    insufficient_hours:   'Insufficient Hours',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[type]}`}>
      {labels[type]}
    </span>
  );
};

const PAGE_SIZE = 50;

const AttendanceAlerts = () => {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate]     = useState(defaultEnd());
  const [allRecords, setAllRecords]   = useState([]);
  const [shifts, setShifts]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // User search filter
  const [allUsers, setAllUsers]               = useState([]);
  const [userSearchTerm, setUserSearchTerm]   = useState('');
  const [selectedUserId, setSelectedUserId]   = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Bulk select
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  // Edit modal
  const [editModalOpen, setEditModalOpen]   = useState(false);
  const [editingRecord, setEditingRecord]   = useState(null);
  const [editTxData, setEditTxData]         = useState([]);
  const [editTxLoading, setEditTxLoading]   = useState(false);
  const [editHistoryData, setEditHistoryData]       = useState([]);
  const [editHistoryLoading, setEditHistoryLoading] = useState(false);

  useEffect(() => {
    usersAPI.getUsers({ per_page: 1000, show_inactive: false })
      .then(r => setAllUsers(r.data.users || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchAlerts(); }, [startDate, endDate, selectedUserId]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setSelectedRecords(new Set());
    setCurrentPage(1);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedUserId) params.user_id = selectedUserId;
      const response = await attendanceAPI.getHrDailyRecords(params);
      setAllRecords(response.data.records || []);
      setShifts(response.data.shifts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  // Filter to only records with at least one issue
  const issueRecords = allRecords.filter(r => getIssueTypes(r).length > 0);

  const filteredRecords = issueRecords.filter(r => {
    if (activeFilter === 'all') return true;
    return getIssueTypes(r).includes(activeFilter);
  });

  // Sort: most recent first, then by staff_no (ascending)
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    const dateDiff = new Date(b.workday) - new Date(a.workday);
    if (dateDiff !== 0) return dateDiff;
    return a.staff_no.localeCompare(b.staff_no);
  });

  const totalPages = Math.ceil(sortedRecords.length / PAGE_SIZE);
  const paginatedRecords = sortedRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Counts per issue type
  const counts = {
    absent:               issueRecords.filter(r => getIssueTypes(r).includes('absent')).length,
    incomplete:           issueRecords.filter(r => getIssueTypes(r).includes('incomplete')).length,
    late:                 issueRecords.filter(r => getIssueTypes(r).includes('late')).length,
    early_out:            issueRecords.filter(r => getIssueTypes(r).includes('early_out')).length,
    insufficient_hours:   issueRecords.filter(r => getIssueTypes(r).includes('insufficient_hours')).length,
  };

  const getRecordKey = (r) => `${r.user_id}_${r.workday}`;

  const toggleSelect = (r) => {
    const key = getRecordKey(r);
    const next = new Set(selectedRecords);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelectedRecords(next);
  };

  // Selects / deselects the current page only
  const toggleAll = () => {
    const pageKeys = paginatedRecords.map(getRecordKey);
    const allPageSelected = pageKeys.every(k => selectedRecords.has(k));
    const next = new Set(selectedRecords);
    if (allPageSelected) {
      pageKeys.forEach(k => next.delete(k));
    } else {
      pageKeys.forEach(k => next.add(k));
    }
    setSelectedRecords(next);
  };

  const handleBulkFinalize = async () => {
    if (selectedRecords.size === 0) return;
    if (!window.confirm(`Finalize ${selectedRecords.size} records?`)) return;
    setLoading(true);
    try {
      const updates = Array.from(selectedRecords).map(key => {
        const [userId, workday] = key.split('_');
        return { user_id: parseInt(userId), workday, is_final: true, reason: 'HR Bulk Finalize (Alerts)' };
      });
      await attendanceAPI.hrBatchUpdateRecords({ updates });
      setSuccess(`Finalized ${selectedRecords.size} records.`);
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finalize records.');
      setLoading(false);
    }
  };

  const toLocalISO = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const openEdit = async (record) => {
    setEditingRecord({ ...record, clock_in: toLocalISO(record.clock_in), clock_out: toLocalISO(record.clock_out), reason: '' });
    setEditTxData([]);
    setEditTxLoading(true);
    setEditHistoryData([]);
    setEditHistoryLoading(true);
    setEditModalOpen(true);

    const histPromise = record.record_id
      ? attendanceAPI.getRecordHistory(record.record_id)
      : Promise.resolve({ data: { history: [] } });

    const [txRes, histRes] = await Promise.allSettled([
      attendanceAPI.getRawTransactions(record.user_id, record.workday),
      histPromise,
    ]);
    setEditTxData(txRes.status === 'fulfilled' ? txRes.value.data.transactions : []);
    setEditTxLoading(false);
    setEditHistoryData(histRes.status === 'fulfilled' ? histRes.value.data.history : []);
    setEditHistoryLoading(false);
  };

  const handleSingleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updates = [{
        user_id: editingRecord.user_id,
        workday: editingRecord.workday,
        clock_in: editingRecord.clock_in || null,
        clock_out: editingRecord.clock_out || null,
        shift_id: editingRecord.shift_id,
        is_final: editingRecord.is_final,
        reason: editingRecord.reason || 'HR Manual Edit (Alerts)',
      }];
      await attendanceAPI.hrBatchUpdateRecords({ updates });
      setSuccess('Record updated.');
      setEditModalOpen(false);
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record.');
      setLoading(false);
    }
  };

  const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';

  const ISSUE_LABELS = { absent: 'Absent', incomplete: 'Incomplete', late: 'Late', early_out: 'Early Out', insufficient_hours: 'Insufficient Hours' };
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleExportCsv = () => {
    if (!sortedRecords.length) return;
    const headers = ['Date', 'Day', 'Name', 'Staff No', 'Shift', 'Clock In', 'Clock Out', 'Hours', 'Issues', 'Finalised'];
    const rows = sortedRecords.map(r => [
      r.workday,
      DAYS[new Date(r.workday + 'T00:00:00').getDay()],
      r.name,
      r.staff_no,
      r.shift_name ? `${r.shift_name} (${r.shift_code})` : 'Unassigned',
      formatTime(r.clock_in),
      formatTime(r.clock_out),
      formatMinutes(r.total_work_minutes),
      getIssueTypes(r).map(t => ISSUE_LABELS[t]).join(', '),
      r.is_final ? 'Yes' : 'No',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alerts_${startDate}_to_${endDate}${activeFilter !== 'all' ? `_${activeFilter}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-6 w-6 text-amber-500" />
          <h2 className="text-xl font-bold text-gray-900">Attendance Alerts</h2>
          {!loading && (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {issueRecords.length} issues
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full md:w-auto">
          {/* User search */}
          <div
            className="relative min-w-[220px]"
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowUserDropdown(false); }}
          >
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => { setUserSearchTerm(e.target.value); setShowUserDropdown(true); }}
              onFocus={() => setShowUserDropdown(true)}
              placeholder="Filter by employee..."
              className="pl-9 pr-8 py-2 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {(userSearchTerm || selectedUserId) && (
              <button
                className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                onMouseDown={(e) => { e.preventDefault(); setUserSearchTerm(''); setSelectedUserId(''); setShowUserDropdown(false); }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showUserDropdown && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
                <li
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-500"
                  onMouseDown={() => { setSelectedUserId(''); setUserSearchTerm(''); setShowUserDropdown(false); }}
                >
                  All Employees
                </li>
                {allUsers
                  .filter(u => !userSearchTerm || u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.staff_no.toLowerCase().includes(userSearchTerm.toLowerCase()))
                  .map(u => (
                    <li
                      key={u.id}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                      onMouseDown={() => { setSelectedUserId(String(u.id)); setUserSearchTerm(`${u.name} (${u.staff_no})`); setShowUserDropdown(false); }}
                    >
                      {u.name} <span className="text-gray-400">({u.staff_no})</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>
          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">To</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>

          <button
            onClick={fetchAlerts}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Refresh</span>
          </button>

          <button
            onClick={handleExportCsv}
            disabled={!sortedRecords.length}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Export</span>
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border-l-4 border-red-400 p-4"><p className="text-sm text-red-700">{error}</p></div>}
      {success && <div className="bg-green-50 border-l-4 border-green-400 p-4"><p className="text-sm text-green-700">{success}</p></div>}

      {/* Summary counts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { key: 'absent',               label: 'Absent',             color: 'red' },
          { key: 'incomplete',           label: 'Incomplete',          color: 'yellow' },
          { key: 'late',                 label: 'Late',                color: 'orange' },
          { key: 'early_out',            label: 'Early Out',           color: 'purple' },
          { key: 'insufficient_hours',   label: 'Insufficient Hours',  color: 'pink' },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => { setActiveFilter(activeFilter === key ? 'all' : key); setCurrentPage(1); }}
            className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors ${
              activeFilter === key ? `border-${color}-400 ring-2 ring-${color}-200` : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <p className="text-sm text-gray-500 font-medium">{label}</p>
            <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{counts[key]}</p>
          </button>
        ))}
      </div>

      {/* Issue type filter pills */}
      <div className="flex space-x-2 flex-wrap gap-y-2">
        {ISSUE_FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => { setActiveFilter(f.id); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeFilter === f.id
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.label}
            {f.id !== 'all' && (
              <span className={`ml-1.5 text-xs ${activeFilter === f.id ? 'text-blue-100' : 'text-gray-400'}`}>
                {counts[f.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      <div className="bg-white p-4 shadow rounded-lg flex justify-between items-center">
        <div className="text-sm text-gray-700">
          <span className="font-semibold">{selectedRecords.size}</span> records selected
        </div>
        <button
          onClick={handleBulkFinalize}
          disabled={selectedRecords.size === 0}
          className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${selectedRecords.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          Finalize Selected
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="relative overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : sortedRecords.length === 0 ? (
            <div className="p-12 text-center">
              <AlertTriangle className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm font-medium text-gray-900">No issues found</p>
              <p className="mt-1 text-sm text-gray-500">All records look clean for this period.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={paginatedRecords.length > 0 && paginatedRecords.every(r => selectedRecords.has(getRecordKey(r)))}
                      onChange={toggleAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock Out</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issues</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRecords.map((record) => {
                  const key = getRecordKey(record);
                  const issues = getIssueTypes(record);
                  return (
                    <tr key={key} className={getRowHighlightClass(record.workday, record.is_final)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRecords.has(key)}
                          onChange={() => toggleSelect(record)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DateColumnDisplay workday={record.workday} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{record.name}</div>
                        <div className="text-sm text-gray-500">{record.staff_no}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.shift_name ? `${record.shift_name} (${record.shift_code})` : 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatTime(record.clock_in)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatTime(record.clock_out)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatMinutes(record.total_work_minutes)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1 items-start">
                          {issues.map(t => <IssueBadge key={t} type={t} />)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs">
                        {record.latest_reason && (
                          <div className="text-gray-700 truncate" title={record.latest_reason}>{record.latest_reason}</div>
                        )}
                        {record.override_remark && (
                          <div className="text-teal-600 truncate" title={record.override_remark}><span className="font-medium">Excused:</span> {record.override_remark}</div>
                        )}
                        {!record.latest_reason && !record.override_remark && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEdit(record)} className="text-blue-600 hover:text-blue-900">
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border border-gray-200 rounded-lg shadow-sm">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
            <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, sortedRecords.length)}</span> of{' '}
            <span className="font-medium">{sortedRecords.length}</span> issues
            {selectedRecords.size > 0 && (
              <span className="ml-2 text-blue-600">({selectedRecords.size} selected across all pages)</span>
            )}
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`px-3 py-1 rounded border text-sm ${
                      currentPage === item
                        ? 'bg-blue-600 border-blue-600 text-white font-medium'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editingRecord && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingRecord.name} — {editingRecord.workday}
                </h3>
                {editingRecord.is_final && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    Finalised
                  </span>
                )}
              </div>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex flex-1 min-h-0">

              {/* Left: Raw Transactions */}
              <div className="w-5/12 border-r border-gray-200 flex flex-col min-h-0">
                <RawTransactionsPanel loading={editTxLoading} data={editTxData} workday={editingRecord.workday} />
              </div>

              {/* Right: Edit Form + Audit Trail */}
              <div className="w-7/12 flex flex-col min-h-0">
                <div className="overflow-y-auto flex-1 min-h-0">
                  {editingRecord.is_final && (
                    <div className="mx-4 mt-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      <p className="text-xs text-amber-700 font-medium">Finalised — fields are read-only. Uncheck "Mark as Final" below to enable editing.</p>
                    </div>
                  )}
                  <form onSubmit={handleSingleUpdate} className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Clock In</label>
                      <input
                        type="datetime-local"
                        step="1"
                        disabled={editingRecord.is_final}
                        value={editingRecord.clock_in || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, clock_in: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Clock Out</label>
                      <input
                        type="datetime-local"
                        step="1"
                        disabled={editingRecord.is_final}
                        value={editingRecord.clock_out || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, clock_out: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shift</label>
                      <select
                        disabled={editingRecord.is_final}
                        value={editingRecord.shift_id || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, shift_id: parseInt(e.target.value) })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      >
                        <option value="">Select Shift</option>
                        {shifts.map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingRecord.is_final || false}
                        onChange={(e) => setEditingRecord({ ...editingRecord, is_final: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">Mark as Final</label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Reason (Audit Trail)</label>
                      <input
                        type="text"
                        required
                        disabled={editingRecord.is_final}
                        placeholder="Required rationale for edit"
                        value={editingRecord.reason}
                        onChange={(e) => setEditingRecord({ ...editingRecord, reason: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div className="pt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setEditModalOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>

                {/* Audit Trail */}
                <div className="border-t border-gray-200 h-48 flex-shrink-0 flex flex-col min-h-0">
                  <AuditTrailPanel loading={editHistoryLoading} data={editHistoryData} />
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAlerts;
